import type { AppRouter } from '@netcon-work/api/router'
import { WORKSPACE_UNBEKANNT } from '@netcon-work/shared'
import { createTRPCClient, httpBatchLink } from '@trpc/client'

/**
 * tRPC-Client. Der Typ kommt direkt aus dem Backend-Router – es gibt kein
 * separates API-Schema, das auseinanderlaufen könnte. Ändert sich dort eine
 * Signatur, bricht hier das Build (Konzept Kap. 8.1).
 */

type Horcher = () => void
const horcher = new Set<Horcher>()

/**
 * **Wenn die Sitzung unterwegs abläuft** (Nils, 04.08.2026).
 *
 * > *„Wenn man nicht angemeldet ist, dann kann man trotzdem die Masken sehen – das ist dann
 * > irgendwie nicht so gut."*
 *
 * Er hat recht, und die Ursache war: **die Anmeldung wurde nur beim Laden geprüft.** Die
 * `AppShell` fragt einmal `auth.me`, bekommt eine Person und zeigt die Anwendung. Läuft das
 * Cookie danach ab – nach einer Nacht, nach einem Neustart des Dienstes –, bleibt alles stehen,
 * was schon im Speicher liegt. Erst beim nächsten Schreibversuch kommt ein „Anmeldung
 * erforderlich", **an der einzelnen Maske statt an der Anwendung.**
 *
 * Das ist die schlechteste Variante: es sieht aus, als sei etwas kaputt, und nicht, als sei man
 * abgemeldet. Und wer nur liest, merkt es überhaupt nicht.
 *
 * Deshalb hört dieser Client auf **401** und meldet es einmal an alle, die es angeht. Die
 * `AppShell` verwirft daraufhin die Sitzung und zeigt die Anmeldemaske – dieselbe Antwort wie
 * beim ersten Laden.
 *
 * ## Warum am Transport und nicht an jeder Abfrage
 *
 * Es gibt über sechzig Prozeduren. Jede einzeln auf „nicht angemeldet" prüfen zu lassen wäre
 * sechzigmal dieselbe Zusage – und die einundsechzigste vergisst man. Dasselbe Muster wie beim
 * Aufgabendetail ([E-56](../../../../docs/ENTSCHEIDUNGEN.md)).
 */
export function beiSitzungVerloren(f: Horcher): () => void {
  horcher.add(f)
  return () => {
    horcher.delete(f)
  }
}

/**
 * **Der aktive Workspace** ([Z-07](../../../../docs/phasen/zusatz-07-workspaces.md)).
 *
 * Steht als Modulvariable und nicht in React-Zustand, weil sie **beim Absenden** gebraucht
 * wird und nicht beim Zeichnen: `headers()` läuft im Transport, außerhalb jedes Bauteils.
 * Ein Kontext-Wert wäre dort nicht zu erreichen, ohne den Client bei jedem Wechsel neu zu
 * bauen – und dann verlöre TanStack Query seinen Zwischenspeicher.
 *
 * ## Und warum das keine Sicherheitslücke ist
 *
 * Weil der Server ihr nicht glaubt. `aktivenWorkspaceAufloesen` prüft die Kopfzeile gegen die
 * Mitgliedschaft und antwortet bei einer fremden Kennung mit `NOT_FOUND`. Was hier steht, ist
 * ein **Wunsch** – die Grenze hängt an der Sitzung, nicht an dieser Zeile.
 */
let aktiverWorkspace: string | null = null

/**
 * **Ein 401 kann in einem 207 stecken** (17.09.2026).
 *
 * `httpBatchLink` bündelt gleichzeitige Abfragen in eine Anfrage. Haben die Antworten darin
 * **verschiedene** Status – etwa `auth.me` mit 200 (`null`) neben einer geschützten Abfrage mit
 * 401 –, antwortet tRPC mit **207 Multi-Status**. Der Blick nur auf `status === 401` übersah
 * genau diesen Fall, und die abgelaufene Sitzung blieb stumm.
 *
 * Gelesen wird eine **Kopie**; die Antwort selbst geht unverändert an tRPC. 207 ist selten,
 * das Parsen kostet also nur dann etwas, wenn ohnehin etwas schiefgeht.
 */
async function enthaelt401(antwort: Response): Promise<boolean> {
  try {
    const inhalt: unknown = await antwort.clone().json()
    const teile = Array.isArray(inhalt) ? inhalt : [inhalt]
    return teile.some((teil) => {
      const data = (teil as { error?: { data?: { httpStatus?: unknown; code?: unknown } } })?.error
        ?.data
      return data?.httpStatus === 401 || data?.code === 'UNAUTHORIZED'
    })
  } catch {
    return false
  }
}

/**
 * **Ein gemerkter Workspace, der nicht mehr gilt, sperrt sonst alles** (17.09.2026).
 *
 * Der Server prüft `x-workspace` schon beim Aufbau des Kontexts und lehnt eine fremde Kennung
 * für **jede** Anfrage ab – auch für `workspaces.meine`, die der Umschalter bräuchte, um den
 * Wert zurückzusetzen. Der Abgleich dort kam also nie zum Zug, und kein Neuladen half: der
 * Wert steht im Browserspeicher. Deshalb wird es hier erkannt, am Transport.
 */
async function workspaceUnbekannt(antwort: Response): Promise<boolean> {
  if (antwort.status !== 404 && antwort.status !== 207) return false
  try {
    const inhalt: unknown = await antwort.clone().json()
    const teile = Array.isArray(inhalt) ? inhalt : [inhalt]
    return teile.some(
      (teil) => (teil as { error?: { message?: unknown } })?.error?.message === WORKSPACE_UNBEKANNT,
    )
  } catch {
    return false
  }
}

/** Derselbe Schlüssel wie `WELT_SPEICHER_SCHLUESSEL` in `welt.ts` – dort steht, warum. */
const WELT_SPEICHER = 'netcon-work.workspace'

/**
 * Umschalten. Ruft **nicht** selbst neu ab: wer wechselt, muss anschließend die Abfragen
 * verwerfen (`queryClient.invalidateQueries()`), und das gehört dorthin, wo der Client der
 * Abfragen liegt. Zwei Stellen, die dasselbe auslösen, wären eine zu viel.
 */
export function workspaceSetzen(id: string | null): void {
  aktiverWorkspace = id
}

export function aktiverWorkspaceIst(): string | null {
  return aktiverWorkspace
}

export const trpc = createTRPCClient<AppRouter>({
  links: [
    httpBatchLink({
      url: '/api/trpc',
      /**
       * **Die Kopfzeile mit dem aktiven Workspace** (Z-07).
       *
       * Fehlt sie, nimmt der Server den ersten eigenen Workspace – das ist der Zustand beim
       * ersten Laden, bevor die Oberfläche weiß, welcher zuletzt gewählt war.
       */
      headers: () => (aktiverWorkspace ? { 'x-workspace': aktiverWorkspace } : {}),
      /**
       * **Eigenes `fetch`, nur um den Status zu sehen.** Ein tRPC-`UNAUTHORIZED` wird zu
       * HTTP 401; die Antwort selbst wird unverändert weitergereicht, damit der Aufrufer
       * seinen Fehler bekommt wie bisher.
       */
      fetch: async (input, init) => {
        /**
         * Die Typen von tRPC und der Browser-API weichen bei `signal` um ein `undefined`
         * voneinander ab (`exactOptionalPropertyTypes`). Weitergereicht wird unverändert –
         * die Umtypung sagt nur, dass hier nichts umgeschrieben wird.
         */
        let antwort = await fetch(input as RequestInfo, init as RequestInit)
        /**
         * **Einmal ohne den gemerkten Workspace wiederholen.** Der Server nimmt dann den ersten
         * eigenen; der Umschalter gleicht danach gegen die Liste ab (`WorkspaceUmschalter`).
         */
        if (aktiverWorkspace && (await workspaceUnbekannt(antwort))) {
          aktiverWorkspace = null
          try {
            globalThis.localStorage?.removeItem(WELT_SPEICHER)
          } catch {
            /* Kein Speicher – dann gilt es eben nur bis zum Neuladen. */
          }
          const kopf = new Headers((init as RequestInit | undefined)?.headers)
          kopf.delete('x-workspace')
          antwort = await fetch(input as RequestInfo, { ...(init as RequestInit), headers: kopf })
        }
        if (antwort.status === 401 || (antwort.status === 207 && (await enthaelt401(antwort)))) {
          for (const f of horcher) f()
        }
        return antwort
      },
    }),
  ],
})
